import { ApiError, syntheticProblem, type Problem } from './problem';
import { uuidv7 } from './uuidv7';

/**
 * The BFF transport.
 *
 * Everything the backend decides about a browser follows from the transport, not from the body
 * (`backend/internal/api/authhandlers.go` §credential):
 *
 *   - the credential is the `__Host-expo_session` cookie, so every request is `credentials: 'same-origin'`
 *     and NO bearer token exists anywhere in this application;
 *   - `__Host-expo_csrf` is deliberately not HttpOnly so first-party script can echo it in
 *     `X-Expo-CSRF` (double submit, doc 07 §3.3) — that is the only cookie this code reads;
 *   - `X-Expo-Client: dashboard/<semver>` (staff) or `portal/<semver>` (customer) names the surface;
 *     both are in the server's `browserSurfaces` set;
 *   - `Idempotency-Key: <uuidv7>` is REQUIRED on every mutating route;
 *   - `If-Match: "<version>"` is REQUIRED on every PATCH/PUT in the tenancy surface;
 *   - a refusal is `application/problem+json` and is surfaced as an `ApiError`, never swallowed.
 *
 * The base is the same-origin `/api` prefix (`EXPO_API_BASE_PATH`, default `/api`); the dev server
 * proxies it to the local backend so the cookie flow is identical in development and production.
 */

export const API_BASE = '/api';

/** The two browser surfaces this repository ships. */
export type ClientSurface = 'dashboard' | 'portal';

export const CLIENT_VERSION = '1.0.0';

const CSRF_COOKIE = '__Host-expo_csrf';

export function readCsrfToken(): string | null {
  const raw = typeof document === 'undefined' ? '' : document.cookie;
  for (const part of raw.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === CSRF_COOKIE) return decodeURIComponent(rest.join('='));
  }
  return null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Multipart body for the media/branding/logo upload routes. */
  formData?: FormData;
  /** The row version the caller read. Sent as `If-Match: "<version>"`. */
  ifMatch?: number | string;
  /**
   * `If-None-Match`, which on this API means one thing: `*`, "create it only if it does not exist".
   * `handleSetHoldPolicy` requires exactly one of the two, because an absent `If-Match` used to
   * mean "create" and two writers who both found no row both won.
   */
  ifNoneMatch?: string;
  /** Overrides the generated idempotency key — used only to replay a request deliberately. */
  idempotencyKey?: string;
  query?: Record<string, string | number | boolean | undefined>;
  surface?: ClientSurface;
  signal?: AbortSignal;
}

export interface ApiResponse<T> {
  data: T;
  /** The `ETag` the server returned, stripped of quotes and the weak marker. */
  etag: string | null;
  /** The row version parsed out of the ETag, when the server sent one. */
  version: number | null;
  status: number;
}

const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Splits an `ETag` into the tag itself and — only when the tag IS one — a row version.
 *
 * NOT EVERY ENTITY TAG ON THIS API IS AN INTEGER (round-2 B2). Three shapes ship today:
 *
 *   - `"7"` — a row version, on every tenancy resource. This is the one `If-Match: "<version>"`
 *     compares numerically;
 *   - `"<uuid>:<status>"` — `catalogue.ReleaseETag`. "A release's bytes are immutable; what moves
 *     is its STATE", so the tag is the pair, and there is no integer version anywhere in it;
 *   - `"<uuid>"` — `catalogue.HoldPolicyETag`, the server-owned revision id.
 *
 * `Number.parseInt` reads a LEADING integer and stops, so a release tag beginning
 * `0192abcd-…` parsed as the version `192`: a number that exists nowhere in the system, sent back
 * as an expected version, and refused (or worse, matched by accident). The rule is therefore that a
 * version is produced only when the WHOLE tag is an integer; every other tag is carried verbatim
 * and echoed verbatim in `If-Match`, which is what the fencing routes actually compare.
 */
function parseEtag(raw: string | null): { etag: string | null; version: number | null } {
  if (!raw) return { etag: null, version: null };
  const cleaned = raw.replace(/^W\//, '').replace(/"/g, '').trim();
  const version = /^\d+$/.test(cleaned) ? Number.parseInt(cleaned, 10) : null;
  return { etag: cleaned, version };
}

/** The surface header value the whole session uses. Set once by the portal shell. */
let activeSurface: ClientSurface = 'dashboard';

export function setActiveSurface(surface: ClientSurface): void {
  activeSurface = surface;
}

export function getActiveSurface(): ClientSurface {
  return activeSurface;
}

/**
 * Mints the `__Host-expo_install` cookie on first contact, exactly once per page.
 *
 * Every authentication endpoint spends a PER-INSTALLATION rate budget, and a caller with no
 * installation gets one minted implicitly — which is why the server refuses a source that keeps
 * asking for new ones: "a client mints one and keeps it" (`iam.admitInstallation`,
 * `InstallPerIPHr = 10`). So the dashboards ask for one deliberately, before the first
 * authentication call. The cookie is `HttpOnly`, so script cannot see whether it already holds one —
 * but the server returns the EXISTING installation when a valid cookie comes back, so a repeat call
 * mints nothing and the once-per-page guard below is a courtesy rather than a correctness device.
 */
let installation: Promise<void> | null = null;

export function ensureInstallation(surface?: ClientSurface): Promise<void> {
  installation ??= apiRequest('/v1/auth/install', {
    method: 'POST',
    ...(surface ? { surface } : {}),
  }).then(
    () => undefined,
    () => undefined, // a refusal here is surfaced by the authentication call that follows
  );
  return installation;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
  const method = options.method ?? 'GET';
  const headers = new Headers();
  headers.set('Accept', 'application/json');
  headers.set('X-Expo-Client', `${options.surface ?? activeSurface}/${CLIENT_VERSION}`);

  if (MUTATING.has(method)) {
    // Double submit: the CSRF cookie is echoed in the header. A request with no cookie yet (the
    // very first login) simply carries no header, and the server's own check decides.
    const csrf = readCsrfToken();
    if (csrf) headers.set('X-Expo-CSRF', csrf);
    headers.set('Idempotency-Key', options.idempotencyKey ?? uuidv7());
  }
  if (options.ifMatch !== undefined && options.ifMatch !== null) {
    headers.set('If-Match', `"${options.ifMatch}"`);
  }
  if (options.ifNoneMatch) {
    headers.set('If-None-Match', options.ifNoneMatch);
  }

  let payload: BodyInit | undefined;
  if (options.formData) {
    payload = options.formData; // the browser sets the multipart boundary
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    payload = JSON.stringify(options.body);
  } else if (MUTATING.has(method)) {
    // Every mutating route reads a body; an action with no arguments still sends `{}` so the
    // idempotency fingerprint is stable across replays.
    headers.set('Content-Type', 'application/json');
    payload = '{}';
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
    redirect: 'error',
  };
  if (payload !== undefined) init.body = payload;
  if (options.signal) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    throw new ApiError(
      method,
      path,
      0,
      syntheticProblem(0, 'transport.unreachable', cause instanceof Error ? cause.message : 'network failure'),
    );
  }

  const { etag, version } = parseEtag(response.headers.get('ETag'));

  if (response.status === 204) {
    return { data: undefined as T, etag, version, status: response.status };
  }

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
  }

  if (!response.ok) {
    const problem: Problem =
      parsed && typeof parsed === 'object' && 'code' in parsed
        ? (parsed as Problem)
        : syntheticProblem(
            response.status,
            'transport.unexpected_response',
            text.slice(0, 240) || response.statusText,
          );
    throw new ApiError(method, path, response.status, problem);
  }

  return { data: parsed as T, etag, version, status: response.status };
}

/** GET helper for the many read screens. */
export async function apiGet<T>(path: string, query?: RequestOptions['query']): Promise<T> {
  const res = await apiRequest<T>(path, query ? { query } : {});
  return res.data;
}

/** The list envelope every collection route returns (`listJSON` in the backend). */
export interface Page<T> {
  items: T[] | null;
  next_cursor?: string;
}

export async function apiList<T>(path: string, query?: RequestOptions['query']): Promise<Page<T>> {
  const page = await apiGet<Page<T>>(path, query);
  return { items: page.items ?? [], ...(page.next_cursor ? { next_cursor: page.next_cursor } : {}) };
}
